import { useEffect, useState } from 'react';
import { ListChecks } from 'lucide-react';
import Modal from '../../../shared/Modal';
import ModalFormActions from '../../../shared/ModalFormActions';
import { CheckboxField, TextInput, SelectInput } from '../../../shared/FormControls';
import { DATA_TYPE_OPTIONS } from './DocumentTypeFieldsConstants';
import { useActiveDocumentTypes} from '../../../../hooks/admin/edmsSettings/documentTypes/useActiveDocumentTypes'


/** Form state and payload match backend: name, required, dataType, documentTypeId */
const initialFormState = {
  name: '',
  required: false,
  dataType: 'string',
  documentTypeId: '',
};

const DocumentTypeFieldModal = ({
  isOpen,
  mode = 'create',
  documentTypeField,
  // documentTypes = [],
  onSubmit,
  onClose,
  isSubmitting = false,
  serverError,
}) => {
  const [formValues, setFormValues] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const { documentTypes } = useActiveDocumentTypes({ enabled: isOpen });



  useEffect(() => {
    if (isOpen) {
      setErrors({});

      if (mode === 'edit' && documentTypeField) {
        const documentTypeId =
          documentTypeField.documentTypeId ??
          documentTypeField.documentType?._id ??
          documentTypeField.documentType ??
          '';
        setFormValues({
          name: documentTypeField.name ?? '',
          required: Boolean(documentTypeField.required),
          dataType: documentTypeField.dataType ?? 'string',
          documentTypeId: String(documentTypeId),
        });
      } else {
        setFormValues(initialFormState);
      }
    }
  }, [isOpen, mode, documentTypeField]);

  const validate = () => {
    const nextErrors = {};

    if (!formValues.name.trim()) {
      nextErrors.name = 'Document type field name is required';
    }
    if (!formValues.documentTypeId.trim()) {
      nextErrors.documentTypeId = 'Document type is required';
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    const value = field === 'required' ? event.target.checked : event.target.value;
    setFormValues((previous) => ({ ...previous, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    const payload = {
      name: formValues.name.trim(),
      required: Boolean(formValues.required),
      dataType: String(formValues.dataType || 'string'),
      documentTypeId: formValues.documentTypeId.trim(),
    };

    onSubmit({ mode, payload });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? 'Edit Document Type Field' : 'Create Document Type Field'}
      size="lg"
      icon={<ListChecks className="h-6 w-6 text-primary-dark" strokeWidth={1.5} />}
      footer={
        <ModalFormActions
          onCancel={onClose}
          formId="document-type-field-form"
          isSubmitting={isSubmitting}
          submitText={mode === 'edit' ? 'Save changes' : 'Create Document Type Field'}
        />
      }
    >
      <form id="document-type-field-form" className="space-y-4" onSubmit={handleSubmit}>
        {serverError ? (
          <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
            {serverError}
          </p>
        ) : null}

        <div className="grid gap-4 md:grid-cols-1">
          <TextInput
            id="name"
            label="Field Name"
            type="text"
            value={formValues.name}
            onChange={handleChange('name')}
            placeholder="e.g. Reference Number"
            maxLength={200}
            disabled={isSubmitting}
            error={errors.name}
          />

          <SelectInput
            id="documentTypeId"
            label="Document Type"
            value={formValues.documentTypeId}
            onChange={handleChange('documentTypeId')}
            disabled={isSubmitting || mode === 'edit'}
            error={errors.documentTypeId}
          >
            <option value="">Select document type</option>
            {documentTypes.map((dt) => (
              <option key={dt._id} value={dt._id}>
                {dt.name ?? dt.code ?? dt._id}
              </option>
            ))}
          </SelectInput>

          {/* <div>
            <label htmlFor="toCurrency" className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              To Currency
            </label>
            <select
              id="toCurrency"
              value={formValues.toCurrency}
              onChange={handleChange('toCurrency')}
              className="mt-1 w-full rounded-lg border border-cream/60 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              disabled={isSubmitting || isLoadingCurrencies}
            >
              <option value="">Select currency</option>
              {activeCurrencies
                .filter((currency) => currency._id !== formValues.fromCurrency)
                .map((currency) => (
                  <option key={currency._id} value={currency._id}>
                    {currency.code} - {currency.name} {currency.symbol ? `(${currency.symbol})` : ''}
                  </option>
                ))}
            </select>
            {errors.toCurrency ? <p className="mt-1 text-xs text-red-600">{errors.toCurrency}</p> : null}
          </div> */}

          <SelectInput
            id="dataType"
            label="Data Type"
            value={formValues.dataType}
            onChange={handleChange('dataType')}
            disabled={isSubmitting}
          >
            {DATA_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </SelectInput>

          <CheckboxField
            id="required"
            label="Required field"
            checked={formValues.required}
            onChange={handleChange('required')}
            disabled={isSubmitting}
            wrapperClassName="pt-6"
          />
        </div>
      </form>
    </Modal>
  );
};

export default DocumentTypeFieldModal;
